import { ColorEnum } from '@/constants/color'
import { InputFieldEnum } from '@/features/ui/inputs/components/InputField/constants'
import { useEnabledInputFieldState } from '@/features/ui/inputs/components/InputField/hooks/useEnabledInputFieldState'
import { useImperativeHandle } from '@/features/ui/inputs/components/InputField/hooks/useImperativeHandle'
import { stylesheet } from '@/features/ui/inputs/components/InputField/styles'
import { InputFieldProps } from '@/features/ui/inputs/components/InputField/types'
import {
    getPlaceholderColor,
    handleContainerPress,
} from '@/features/ui/inputs/components/InputField/utils'
import { FC, useRef } from 'react'
import { Pressable, TextInput } from 'react-native'
import { useStyles } from 'react-native-unistyles'

const InputField: FC<InputFieldProps> = ({
    disabled = false,
    error = false,
    multiline = false,
    FrontComponent,
    TailComponent,
    onChange,
    onFocusChange,
    inputStyle,
    style,
    ref,
    ...rest
}) => {
    const textInputRef = useRef<TextInput>(null)

    useImperativeHandle(ref, textInputRef)

    const { state: enabledState, eventHandlers } = useEnabledInputFieldState()

    const state = disabled ? InputFieldEnum.Disabled : error ? InputFieldEnum.Error : enabledState

    const { styles } = useStyles(stylesheet, {
        state: state !== InputFieldEnum.Default ? state : undefined,
    })

    const handleFocus = () => {
        eventHandlers.onFocus()

        if (onFocusChange) onFocusChange(true)
    }

    const handleBlur = () => {
        eventHandlers.onBlur()

        if (onFocusChange) onFocusChange(false)
    }

    return (
        <Pressable
            disabled={disabled}
            onMouseEnter={eventHandlers.onMouseEnter}
            onMouseLeave={eventHandlers.onMouseLeave}
            onPress={() => handleContainerPress(textInputRef)}
            style={[styles.container, style]}
        >
            {FrontComponent}
            <TextInput
                {...rest}
                key={multiline ? 'multiline' : 'line'}
                {...(!multiline && { numberOfLines: 1 })}
                multiline={multiline}
                textAlignVertical={multiline ? 'top' : 'center'}
                readOnly={disabled}
                onChangeText={(text) => onChange(text)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                cursorColor={ColorEnum.BLUE_500}
                placeholderTextColor={getPlaceholderColor(state)}
                style={[styles.textInput, inputStyle]}
                ref={textInputRef}
            />
            {TailComponent}
        </Pressable>
    )
}

export default InputField
